// Mock API entrypoint for the patient portal. Phase F1–F7 build the frontend
// against these mocks; Phase B1+ replaces them with real /api/secure/patient/...
// endpoints. Contracts must match docs/PATIENT_PORTAL_PLAN.md §5.2 exactly so
// cutover is a flag flip per resource.
//
// All mocks observe the currently-selected PatientProfile (passed in or read
// from localStorage) to prove the multi-patient model works end-to-end before
// any real endpoint exists.
//
// Two layers decide whether mocks or real APIs are used:
//   1. Runtime override (localStorage `patientPortal.useMocks`): set by the
//      shiva=shakti debug panel — wins if present. Values: 'true' | 'false'.
//   2. Build-time default (Vite env `VITE_PATIENT_PORTAL_USE_MOCKS`): baked
//      into the bundle. Currently set to 'false' in .env.production so prod
//      builds default to real APIs.
// Absence of both keeps mocks on as a fail-safe.

const SIMULATED_LATENCY_MS = 220

export function delay(ms = SIMULATED_LATENCY_MS) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export const PATIENT_PORTAL_MOCK_FLAG = 'VITE_PATIENT_PORTAL_USE_MOCKS'
export const PATIENT_PORTAL_MOCK_OVERRIDE_KEY = 'patientPortal.useMocks'

/**
 * Read the localStorage runtime override. Returns 'true' | 'false' | null.
 * null means "no override — fall back to the env-var default".
 */
export function getMocksOverride() {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
    return null
  }
  try {
    const v = window.localStorage.getItem(PATIENT_PORTAL_MOCK_OVERRIDE_KEY)
    return v === 'true' || v === 'false' ? v : null
  } catch (e) {
    return null
  }
}

/**
 * Write the localStorage runtime override. Pass null to clear.
 * Caller is responsible for reloading the page afterwards if it wants
 * already-mounted components to re-fetch through the new path.
 */
export function setMocksOverride(value) {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
    return
  }
  try {
    if (value === null || value === undefined) {
      window.localStorage.removeItem(PATIENT_PORTAL_MOCK_OVERRIDE_KEY)
      return
    }
    if (value !== 'true' && value !== 'false') {
      return
    }
    window.localStorage.setItem(PATIENT_PORTAL_MOCK_OVERRIDE_KEY, value)
  } catch (e) {
    // localStorage write can throw in strict-incognito modes — silent no-op
    // is fine; the user just won't see the override stick.
  }
}

/** Returns whether the build-time env var defaults to mocks-on. */
export function getMocksEnvDefault() {
  // Must use a static string literal — Vite only substitutes import.meta.env.*
  // at build time when the key is a compile-time constant. Dynamic bracket
  // access (e.g. import.meta.env[someVar]) is not resolved in production
  // bundles, leaving the value undefined and silently enabling mocks.
  return import.meta.env.VITE_PATIENT_PORTAL_USE_MOCKS !== 'false'
}

export function shouldUseMocks() {
  const override = getMocksOverride()
  if (override !== null) {
    return override === 'true'
  }
  return getMocksEnvDefault()
}
